import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {MenuItemFormTemplate} from "../../form-components/MenuItemFormTemplate";
import {
    fetchMenuItem,
    postDish, setActiveTab,
    setAvailable,
    setCategory,
    setCategoryId,
    setChosenAllergens,
    setChosenBanners,
    setChosenLabels,
    setCreated,
    setCreatedBy,
    setDescription,
    setDisplayOrder,
    setErrorData,
    setErrorMessage,
    setHasImage,
    setId,
    setMenuItemUpdated,
    setName,
    setPrice,
    setPromoPrice,
    setUpdated,
    setVariants
} from "../../../../../slices/dishFormSlice";
import {setEditDishFormActive} from "../../../../../slices/dishesCategoriesSlice";
import {setChosenAdditions} from "../../../../../slices/dishAdditionsSlice";
import {useClearForm} from "../../../../../hooks/useClearForm";
import {MenuItemFormWrapper} from "./MenuItemFormWrapper";
import {Variants} from "./variants/Variants";
import {useTranslatableTransformer} from "../../../../../hooks/useTranslatableTransformer";
import {useImageExists} from "../../../../../hooks/useImageExists";
import {useConfirmationMessage} from "../../../../../hooks/useConfirmationMessage";
import {useGetTranslation} from "../../../../../hooks/useGetTranslation";
import {usePopulateMenuItemFormCollections} from "../../../../../hooks/usePopulateMenuItemFormCollections";
import {MenuItemMobilePreview} from "./preview/menu-item-details/MenuItemMobilePreview";
import {fetchActiveMenu} from "../../../../../slices/cmsSlice";
import {s3BucketUrl} from "../../../../../apiData";

export const EditMenuItemForm = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const getTranslation = useGetTranslation();
    const {restaurant} = useSelector(state => state.dashboard.view);
    const restaurantLanguage = restaurant?.value.settings.language.toLowerCase();
    const {category, dish} = useSelector(state => state.dishesCategories.view);
    const {activeTab, name} = useSelector(state => state.dishForm.form);
    const clearForm = useClearForm();
    const {data} = useSelector(state => state.dishForm.fetchMenuItem);
    const item = data?.menuItemFormDTO;
    const [file, setFile] = useState(null);
    const imageExists = useImageExists(dish);
    const translatableTransformers = {
        transformName: useTranslatableTransformer({obj: dish, key: 'name'}),
        transformDescription: useTranslatableTransformer({obj: dish, key: 'description'})
    };
    const renderConfirmation = useConfirmationMessage(setMenuItemUpdated);
    const populateFormCollections = usePopulateMenuItemFormCollections();
    const {id, updated, hasImage, isImageCleared} = useSelector(state => state.dishForm.form);

    const previewImage = useMemo(() => {
        if (file) return file;
        if (!isImageCleared && hasImage && id) {
            return `${s3BucketUrl}/menuItems/${id}.webp?t=${updated}`;
        }
        return null;
    }, [file, isImageCleared, hasImage, id, updated]);

    useEffect(() => {
        dispatch(fetchMenuItem({id: dish?.id}));
    }, [dispatch, dish]);

    useEffect(() => {
        const setInitialFormState = () => {
            if (!item) return;

            populateFormCollections();

            dispatch(setId(item.id));
            dispatch(setCategoryId(item.categoryId));
            dispatch(setCategory(category));
            dispatch(setDisplayOrder(item.displayOrder));
            dispatch(setName(item.name[restaurantLanguage]));
            dispatch(setDescription(item.description[restaurantLanguage]));
            dispatch(setVariants(item.variants));
            dispatch(setPrice(item.price.toFixed(2)));
            dispatch(setPromoPrice(item.promoPrice?.toFixed(2)));
            dispatch(setAvailable(item.available));
            dispatch(setChosenBanners(item.banners?.map(banner => ({
                value: banner,
                label: getTranslation(banner.name)
            }))));
            dispatch(setChosenLabels(item.labels?.map(label => ({
                value: label,
                label: getTranslation(label.name)
            }))));
            dispatch(setChosenAllergens(item.allergens?.map(allergen => ({
                value: allergen,
                label: getTranslation(allergen.name)
            }))));
            dispatch(setChosenAdditions(item.additionalIngredients?.map(addition => ({
                value: addition,
                label: addition.name[restaurantLanguage]
            }))));
            dispatch(setCreated(item.created));
            dispatch(setCreatedBy(item.createdBy));
            dispatch(setUpdated(item.updated));
            dispatch(setHasImage(imageExists));
        }
        setInitialFormState();
    }, [dispatch, item, category, imageExists, restaurantLanguage]);

    const handleFormDiscard = () => {
        clearForm();
        setFile(null);
        dispatch(setEditDishFormActive(false));
    }

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        dispatch(setErrorMessage(null));

        const isNameBlank = !name || name.trim().length === 0;
        if (isNameBlank) {
            dispatch(setErrorData({name: t('constraints.NotBlank')}));
            dispatch(setActiveTab('information'));
            return;
        }

        const dishAction = await dispatch(postDish({
            action: 'update',
            file: file,
            translatableTransformers: translatableTransformers
        }));
        if (postDish.fulfilled.match(dishAction)) {
            dispatch(setEditDishFormActive(false));
            dispatch(fetchActiveMenu());
            clearForm();
            setFile(null);
            renderConfirmation();
        } else if (postDish.rejected.match(dishAction)) {
            dispatch(setErrorData(dishAction.payload));
            dispatch(setErrorMessage(dishAction.payload));
            dispatch(setActiveTab('information'));
        }
    };

    const renderFormView = () => {
        switch (activeTab) {
            case 'information':
                return (<MenuItemFormTemplate setFile={setFile} file={file}/>);
            case 'variants':
                return (<Variants/>);
            default:
                return (<MenuItemFormTemplate setFile={setFile} file={file}/>);
        }
    }

    return (
        <MenuItemFormWrapper title={t('editPosition')}
                             onFormSubmit={handleFormSubmit}
                             onFormDiscard={handleFormDiscard}
                             previewContent={<MenuItemMobilePreview image={previewImage}/>}>
            {renderFormView()}
        </MenuItemFormWrapper>
    );
}